import i18n from './i18n';
import _get from 'lodash/get';
import {privilegeUtils} from './privilegeUtils';
import smbUtils from './smbUtils';

const skincareSolutions = [
  {
    value: 'skin_analysis',
    label: i18n.t('u.s.skincare.skin.analysis', 'Skin Analysis'),
  },
  {
    value: 'aesthetic_simulation',
    label: i18n.t('u.s.skincare.aesthetic.simulation', 'Aesthetic Simulation'),
  },
];

const solutionUtils = {
  isSkinAnalysis: auth => privilegeUtils.isSkinAnalysis(auth),
  isSimulation: auth =>
    privilegeUtils.isSimulation(auth) && !smbUtils.isCnSite(auth),
  isOnlySimulation: auth =>
    solutionUtils.isSimulation(auth) &&
    !solutionUtils.isMultipleSolutions(auth),
  isMultipleSolutions: auth =>
    solutionUtils.isSkinAnalysis(auth) && solutionUtils.isSimulation(auth),
};

const multipleSubscriptionsUtils = {
  getSolutionTypes: auth =>
    _get(auth, 'subscriptionItems', []).map(s =>
      _get(s, 'subscription.solutionType')
    ),
  getFunctionTypeBySolution: solution =>
    multipleSubscriptionsUtils.getSolutionTypeInfo(solution).functionType,
  getSolutionTypeInfo: solution => {
    switch (solution) {
      case 'EYEWEAR': {
        return {
          solutionType: 'EYEWEAR',
          solutionName: i18n.t('p.e.eyewear.title', 'Eyewear'),
          functionType: 'EYEWEAR',
          functionTypeName: i18n.t('p.e.eyewear.title', 'Eyewear'),
          comPage: 'eye-wear',
        };
      }
      case 'SKIN_ANALYSIS': {
        return {
          solutionType: 'SKIN_ANALYSIS',
          solutionName: i18n.t('u.s.skincare.skin.analysis', 'Skin Analysis'),
          functionType: 'SKINCARE_PRO',
          functionTypeName: i18n.t(
            'c.l.sidebar.1100.skincarepro.title',
            'Skincare Pro'
          ),
          comPage: 'skincare-pro',
        };
      }
      case 'AESTHETIC_SIMULATION': {
        return {
          solutionType: 'AESTHETIC_SIMULATION',
          solutionName: i18n.t(
            'u.s.skincare.aesthetic.simulation',
            'Aesthetic Simulation'
          ),
          functionType: 'SKINCARE_PRO',
          functionTypeName: i18n.t(
            'c.l.sidebar.1100.skincarepro.title',
            'Skincare Pro'
          ),
          comPage: 'aesthetic-simulation',
        };
      }
      default:
        return {
          solutionType: 'MAKEUP',
          solutionName: i18n.t('c.l.sidebar.1100.makeup.title', 'Makeup'),
          functionType: 'MAKEUP',
          functionTypeName: i18n.t('c.l.sidebar.1100.makeup.title', 'Makeup'),
          comPage: 'makeup',
        };
    }
  },
  getSolutionInfos: auth => {
    const solutionInfos = {};
    const subscriptionItems = _get(auth, 'subscriptionItems', []);

    for (let i = 0; i < subscriptionItems.length; i++) {
      const subscriptionItem = subscriptionItems[i];
      const subscription = _get(subscriptionItem, 'subscription', []);

      solutionInfos[subscription.solutionType] = {
        attrs:
          subscription.solutionType === 'SKIN_ANALYSIS'
            ? _get(subscriptionItem, 'attrs.skincareProConcerns', '')
            : _get(subscriptionItem, 'attrs.skincareTreatments', ''),
        enterpriseSettings:
          subscription.solutionType === 'SKIN_ANALYSIS'
            ? _get(
                subscriptionItem,
                'attrs.skincareProSettings',
                JSON.parse('[]')
              )
            : _get(
                subscriptionItem,
                'attrs.skincareProSettings',
                JSON.parse('[]')
              ),
        isTrialPlan: !!subscription.isTrialPlan,
        isSubscriptionPlan:
          subscription.subscriptionType === 'ESSENTIAL' ||
          subscription.subscriptionType === 'PREMIUM',
        isEnterprisePlan: subscription.subscriptionType === 'ENTERPRISE',
        isPremiumPlan: subscription.isPremiumPlan,
        subscriptionType: subscription.subscriptionType,
        recurringType: subscription.recurringType,
        overdue: !!subscriptionItem.overdue,
        beginTime: subscriptionItem.beginTime,
        endTime: subscriptionItem.endTime,
        leftDays: _get(subscriptionItem, 'leftDays', 0),
        maxNumOfProducts: subscriptionItem.numOfProducts,
        maxNumOfReports: subscription.maxNumberOfReports,
        maxNumOfLicenses: subscription.maxNumberOfLicenses,
        totalMaxNumOfLicenses: multipleSubscriptionsUtils.getTotalMaxNumberOfLicenses(
          auth
        ),
        totalMaxNumOfStaffs: multipleSubscriptionsUtils.getTotalMaxNumberOfStaffs(
          auth
        ),
      };
    }
    return solutionInfos;
  },
  getTotalMaxNumberOfStaffs: auth =>
    _get(auth, 'subscriptionItems', []).reduce((total, current) => {
      if (
        !_get(current, 'overdue', true) &&
        !_get(current, 'subscription.isTrialPlan', true)
      ) {
        return total + _get(current, 'numOfStaffs', 0);
      }
      return total;
    }, 0),
  getTotalMaxNumberOfLicenses: auth =>
    _get(auth, 'subscriptionItems', []).reduce((total, current) => {
      if (!_get(current, 'overdue', true)) {
        return total + _get(current, 'subscription.maxNumberOfLicenses', 0);
      }
      return total;
    }, 0),
  getMaxNumberOfLicenses: (auth, solutionType) => {
    const subscriptionItems = _get(auth, 'subscriptionItems', []);
    for (let i = 0; i < subscriptionItems.length; i++) {
      if (
        _get(subscriptionItems[i], 'subscription.solutionType', '') ===
        solutionType
      ) {
        return _get(subscriptionItems[i], 'numOfLicenses', 0);
      }
    }
    return 0;
  },
  overdueSolutions: auth => {
    const subscriptionItems = _get(auth, 'subscriptionItems', []);
    const available = [];
    for (let i = 0; i < subscriptionItems.length; i++) {
      if (_get(subscriptionItems[i], 'overdue', false)) {
        available.push(_get(subscriptionItems[i], 'subscription.solutionType'));
      }
    }
    return available;
  },
  isAvailableSubscriptionAllTrials: auth => {
    const subscriptionItems = _get(auth, 'subscriptionItems', []);
    if (subscriptionItems.length === 0) return true;
    const available = [];
    const availableTrial = [];
    for (let i = 0; i < subscriptionItems.length; i++) {
      if (!_get(subscriptionItems[i], 'overdue', false)) {
        available.push(_get(subscriptionItems[i], 'subscription.solutionType'));
      }
      if (
        !_get(subscriptionItems[i], 'overdue', false) &&
        _get(subscriptionItems[i], 'subscription.isTrialPlan', false)
      ) {
        availableTrial.push(
          _get(subscriptionItems[i], 'subscription.solutionType')
        );
      }
    }
    return available.length === availableTrial.length;
  },
  isSubscriptionOverdueAreTrials: auth => {
    const subscriptionItems = _get(auth, 'subscriptionItems', []);
    if (subscriptionItems.length === 0) return false;
    const overduePlan = [];
    const overdueTrialPlan = [];
    for (let i = 0; i < subscriptionItems.length; i++) {
      if (_get(subscriptionItems[i], 'overdue', false)) {
        overduePlan.push(
          _get(subscriptionItems[i], 'subscription.solutionType')
        );
      }
      if (
        _get(subscriptionItems[i], 'overdue', false) &&
        _get(subscriptionItems[i], 'subscription.isTrialPlan', false)
      ) {
        overdueTrialPlan.push(
          _get(subscriptionItems[i], 'subscription.solutionType')
        );
      }
    }
    return overduePlan.length === overdueTrialPlan.length;
  },
  getLicenseCount: (list = []) => {
    if (!Array.isArray(list) || list.length === 0) return {};
    let count = {};
    list.forEach(device => {
      let solutionType = device.solutionType;
      if (count[solutionType]) {
        count[solutionType]++;
      } else {
        count[solutionType] = 1;
      }
    });

    return count;
  },
};

export {skincareSolutions, solutionUtils, multipleSubscriptionsUtils};
